import { redirect } from '@tanstack/react-router'
import type { Permission } from '@/types/permissions'
import type { RouterAuthContext } from './router-context'

/**
 * Check if user has a permission in their current studio context
 */
function hasPermission(
  context: RouterAuthContext,
  permission: Permission
): boolean {
  const session = context.session
  if (!session?.currentStudio) return false

  return session.currentStudio.permissions.includes(permission)
}

/**
 * Check if user has any of the specified permissions
 */
function hasAnyPermission(
  context: RouterAuthContext,
  permissions: Permission[]
): boolean {
  return permissions.some((p) => hasPermission(context, p))
}

/**
 * Route guard: Require authentication
 * Use in route's beforeLoad
 *
 * @param context - Router context with auth state
 * @param currentPath - Current route path for redirect after login
 */
export async function requireAuth(
  context: RouterAuthContext,
  currentPath?: string
) {
  // If still loading auth, suspend navigation
  // This prevents flickering and premature redirects
  if (context.isLoading) {
    return
  }

  // If not authenticated and auth is ready, redirect to login
  if (!context.isAuthenticated) {
    throw redirect({
      to: '/auth/login',
      search: { redirect: currentPath ?? '/app/dashboard' },
    })
  }

  // Wait for session to hydrate
  // Session loading is tracked separately from auth0 loading
  if (!context.session) {
    return
  }

  return {
    session: context.session,
  }
}

/**
 * Route guard: Require specific permission(s)
 * Use after requireAuth or in routes that need permission checks
 *
 * @param context - Router context with auth state
 * @param permission - Single permission required
 * @param options - Configuration for redirect behavior
 */
export async function requirePermission(
  context: RouterAuthContext,
  permission: Permission,
  options?: {
    redirectTo?: string
    currentPath?: string
  }
) {
  // First ensure user is authenticated
  await requireAuth(context, options?.currentPath)

  // If auth check passed but no session yet, wait
  if (!context.session) {
    return
  }

  // Check permission
  if (!hasPermission(context, permission)) {
    throw redirect({
      to: options?.redirectTo ?? '/app/dashboard',
      search: {
        error: 'insufficient_permissions',
      },
    })
  }

  return {
    session: context.session,
  }
}

/**
 * Route guard: Require any of the specified permissions
 * Useful for routes accessible to multiple role types
 *
 * @param context - Router context with auth state
 * @param permissions - Array of permissions (OR logic)
 * @param options - Configuration for redirect behavior
 */
export async function requireAnyPermission(
  context: RouterAuthContext,
  permissions: Permission[],
  options?: {
    redirectTo?: string
    currentPath?: string
  }
) {
  // First ensure user is authenticated
  await requireAuth(context, options?.currentPath)

  // If auth check passed but no session yet, wait
  if (!context.session) {
    return
  }

  // Check if user has any of the permissions
  if (!hasAnyPermission(context, permissions)) {
    throw redirect({
      to: options?.redirectTo ?? '/app/dashboard',
      search: {
        error: 'insufficient_permissions',
      },
    })
  }

  return {
    session: context.session,
  }
}

/**
 * Route guard: Redirect if already authenticated
 * Use on public auth pages (login, signup)
 */
export async function redirectIfAuthenticated(
  context: RouterAuthContext,
  redirectTo = '/app/dashboard'
) {
  // Wait for auth to finish loading
  if (context.isLoading) {
    return
  }

  if (context.isAuthenticated && context.session) {
    throw redirect({
      to: redirectTo,
    })
  }
}
